<template>
  <div>
    <!-- Botão de abertura -->
    <div class="text-end mb-2">
      <button class="attention-panel-open-btn" @click="open">
        <i class="fas fa-heartbeat mr-2"></i>
        Painel de Atenção Clínica
        <span v-if="hasCritical" class="attention-panel-open-badge">!</span>
      </button>
    </div>

    <!-- Modal fullscreen -->
    <div v-if="showPanel" class="attention-panel-overlay">
      <div class="attention-panel-container">
        <!-- Header -->
        <div class="attention-panel-header">
          <div class="attention-panel-header-left">
            <i class="fas fa-heartbeat mr-2" style="font-size: 22px"></i>
            <div>
              <h2 class="m-0">Painel de Atenção Clínica</h2>
              <p class="m-0 attention-panel-subtitle">
                {{ residents.length }} residente(s) com risco identificado
                <span v-if="lastUpdate">· Atualizado {{ lastUpdate }}</span>
              </p>
            </div>
          </div>
          <button class="attention-panel-close-btn" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Corpo -->
        <div class="attention-panel-body">
          <!-- Cards de resumo -->
          <div class="attention-panel-summary" v-if="!loading">
            <div class="summary-card summary-critico">
              <div class="summary-number">{{ summary.critical }}</div>
              <div class="summary-label">
                <i class="fas fa-skull-crossbones mr-1"></i>IGA Crítico
              </div>
            </div>
            <div class="summary-card summary-alto">
              <div class="summary-number">{{ summary.high }}</div>
              <div class="summary-label">
                <i class="fas fa-exclamation-triangle mr-1"></i>IGA Alto
              </div>
            </div>
            <div class="summary-card summary-queda">
              <div class="summary-number">{{ summary.fall_risk }}</div>
              <div class="summary-label">
                <i class="fas fa-walking mr-1"></i>Risco Queda Alto
              </div>
            </div>
            <div class="summary-card summary-nutricao">
              <div class="summary-number">{{ summary.nutrition_risk }}</div>
              <div class="summary-label">
                <i class="fas fa-utensils mr-1"></i>Risco Nutricional Alto
              </div>
            </div>
          </div>

          <!-- Filtros -->
          <div class="attention-panel-filters" v-if="!loading">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-bed filter-icon"></i>
                Ala/Setor
              </label>
              <v-select
                v-model="ward_or_sector"
                :items="wardOptions"
                dense
                outlined
                hide-details
                class="modern-select"
                item-value="ward_or_sector"
                :item-text="
                  (v) =>
                    v.ward_or_sector
                      ? `(ALA/Setor) ${v.ward_or_sector}`
                      : 'Todos os setores'
                "
                @change="load"
              />
            </div>

            <div class="filter-group" v-if="false">
              <label class="filter-label">
                <i class="fas fa-filter filter-icon"></i>
                IGA Mínimo
              </label>
              <v-select
                v-model="igaFilter"
                :items="igaFilterOptions"
                dense
                outlined
                hide-details
                class="modern-select"
                item-value="value"
                item-text="text"
                @change="onIgaFilterChange"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-search filter-icon"></i>
                Buscar residente
              </label>
              <v-text-field
                v-model="search"
                dense
                outlined
                hide-details
                placeholder="Nome do residente"
                class="modern-input"
                @input="applyFilters"
              />
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="attention-panel-loading">
            <b-spinner class="mr-3" />
            <span>Carregando dados clínicos...</span>
          </div>

          <!-- Lista de residentes -->
          <div
            v-if="!loading && filteredResidents.length > 0"
            class="attention-panel-table-wrapper"
          >
            <table class="attention-panel-table">
              <thead>
                <tr>
                  <th class="col-resident">Residente</th>
                  <th class="col-score">
                    <i class="fas fa-walking mr-1"></i>Queda
                  </th>
                  <th class="col-score">
                    <i class="fas fa-utensils mr-1"></i>Nutrição
                  </th>
                  <th class="col-score">
                    <i class="fas fa-heartbeat mr-1"></i>Clínico
                  </th>
                  <th class="col-iga">IGA</th>
                  <th class="col-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="resident in filteredResidents"
                  :key="resident.id"
                  :class="rowClass(resident.iga_level)"
                >
                  <td class="col-resident">
                    <div class="resident-cell">
                      <img
                        :src="
                          resident.photo_url || require('../../assets/user.png')
                        "
                        :alt="resident.name"
                        class="resident-avatar"
                      />
                      <div class="resident-info">
                        <span class="resident-name">{{ resident.name }}</span>
                        <span
                          v-if="resident.ward_or_sector"
                          class="resident-sector"
                        >
                          <i class="fas fa-bed mr-1"></i
                          >{{ resident.ward_or_sector }}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td class="col-score">
                    <span
                      class="risk-chip"
                      :class="fallChipClass(resident.fall_level)"
                    >
                      {{ fallLabel(resident.fall_level) }}
                      <small v-if="resident.fall_score !== null"
                        >{{ resident.fall_score }}pt</small
                      >
                    </span>
                  </td>

                  <td class="col-score">
                    <span
                      class="risk-chip"
                      :class="nutritionChipClass(resident.nutrition_level)"
                    >
                      {{ nutritionLabel(resident.nutrition_level) }}
                      <small v-if="resident.nutrition_score !== null"
                        >{{ resident.nutrition_score }}pt</small
                      >
                    </span>
                  </td>

                  <td class="col-score">
                    <span
                      class="risk-chip"
                      :class="clinicalChipClass(resident.clinical_level)"
                    >
                      {{ clinicalLabel(resident.clinical_level) }}
                      <small v-if="resident.clinical_score !== null"
                        >{{ resident.clinical_score }}pt</small
                      >
                    </span>
                  </td>

                  <td class="col-iga">
                    <div class="iga-cell">
                      <span
                        class="iga-value"
                        :class="igaValueClass(resident.iga_level)"
                        >{{ resident.iga }}</span
                      >
                      <span
                        class="iga-label"
                        :class="igaLabelClass(resident.iga_level)"
                        >{{ igaLabel(resident.iga_level) }}</span
                      >
                    </div>
                  </td>

                  <td class="col-action">
                    <button
                      class="btn-ver-residente"
                      @click="goToResident(resident.id)"
                    >
                      <i class="fas fa-external-link-alt mr-1"></i>Ver
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="
              !loading && filteredResidents.length === 0 && residents.length > 0
            "
            class="attention-panel-empty"
          >
            <i class="fas fa-filter"></i>
            <p>Nenhum residente encontrado com os filtros aplicados.</p>
          </div>

          <div
            v-if="!loading && residents.length === 0"
            class="attention-panel-empty"
          >
            <i class="fas fa-check-circle" style="color: #059669"></i>
            <p>Nenhum residente com risco identificado no momento.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import moment from "moment";
import routes from "../../routes";

// Definidos fora do componente para evitar recriação a cada render
const IGA_ORDER = { baixo: 0, moderado: 1, alto: 2, critico: 3 };
const IGA_MIN_LEVEL = { all: 0, moderado: 1, alto: 2, critico: 3 };

export default {
  name: "AttentionPanel",

  data() {
    return {
      showPanel: false,
      loading: false,
      residents: [],
      filteredResidents: [], // FIX: data reativa, não computed
      summary: { critical: 0, high: 0, fall_risk: 0, nutrition_risk: 0 },
      lastUpdate: null,
      ward_or_sector: "",
      wardOptions: [{ ward_or_sector: "" }],
      search: "",
      igaFilter: "all",
      igaFilterOptions: [
        { value: "all", text: "Todos os níveis" },
        { value: "moderado", text: "Moderado ou acima" },
        { value: "alto", text: "Alto ou acima" },
        { value: "critico", text: "Somente Crítico" },
      ],
    };
  },

  computed: {
    hasCritical() {
      return this.summary.critical > 0;
    },
  },

  methods: {
    async open() {
      this.showPanel = true;
      await this.loadWards();
      await this.load();
    },

    close() {
      this.showPanel = false;
    },

    async load() {
      try {
        this.loading = true;
        const params = {};
        if (this.ward_or_sector) params.ward_or_sector = this.ward_or_sector;

        const response = await api.get("/ai/smart/attention-panel", { params });

        if (response.data.success) {
          this.residents = response.data.residents;
          this.summary = response.data.summary;
          this.lastUpdate = moment().format("HH:mm");
          this.applyFilters();
        }
      } catch (error) {
        console.error("[AttentionPanel] erro ao carregar:", error);
        this.$toast.error("Erro ao carregar painel de atenção.");
      } finally {
        this.loading = false;
      }
    },

    async loadWards() {
      try {
        const response = await api.get("/rest-home/bed/ward");
        const { beds } = response.data;
        this.wardOptions = [{ ward_or_sector: "" }, ...beds];
      } catch (error) {
        console.error("[AttentionPanel] erro ao carregar setores:", error);
      }
    },

    // FIX BUG FILTRO: atribuição direta em this.filteredResidents
    // garante reatividade explícita ao resetar para "all"
    applyFilters() {
      let list = [...this.residents];

      const term = this.search.trim().toLowerCase();
      if (term) {
        list = list.filter((r) => r.name.toLowerCase().includes(term));
      }

      // FIX: usa ?? em vez de || para não confundir nível "baixo"
      // (valor numérico 0) com ausência de valor
      const minLevel = IGA_MIN_LEVEL[this.igaFilter] ?? 0;
      list = list.filter((r) => (IGA_ORDER[r.iga_level] ?? 0) >= minLevel);

      this.filteredResidents = list;
    },

    // FIX: $nextTick garante que v-model do v-select já foi atualizado
    // antes de aplicar o filtro
    onIgaFilterChange() {
      this.$nextTick(() => this.applyFilters());
    },

    goToResident(id) {
      this.close();
      routes.push({
        name: "resident",
        params: { id },
        query: { tab: "check", nav: "check" },
      });
    },

    rowClass(igaLevel) {
      return {
        "row-critico": igaLevel === "critico",
        "row-alto": igaLevel === "alto",
        "row-moderado": igaLevel === "moderado",
      };
    },

    fallChipClass(level) {
      if (level === "vermelho") return "chip-vermelho";
      if (level === "amarelo") return "chip-amarelo";
      return "chip-verde";
    },
    fallLabel(level) {
      if (level === "vermelho") return "Alto";
      if (level === "amarelo") return "Moderado";
      return "Baixo";
    },

    nutritionChipClass(level) {
      if (level === "alto") return "chip-vermelho";
      if (level === "moderado") return "chip-amarelo";
      return "chip-verde";
    },
    nutritionLabel(level) {
      if (level === "alto") return "Alto";
      if (level === "moderado") return "Moderado";
      if (!level) return "—";
      return "Baixo";
    },

    clinicalChipClass(level) {
      if (level === "critico") return "chip-critico";
      if (level === "alto") return "chip-vermelho";
      if (level === "moderado") return "chip-amarelo";
      return "chip-verde";
    },
    clinicalLabel(level) {
      if (level === "critico") return "Crítico";
      if (level === "alto") return "Alto";
      if (level === "moderado") return "Moderado";
      if (!level) return "—";
      return "Baixo";
    },

    igaValueClass(level) {
      if (level === "critico") return "iga-critico";
      if (level === "alto") return "iga-alto";
      if (level === "moderado") return "iga-moderado";
      return "iga-baixo";
    },
    igaLabelClass(level) {
      return this.igaValueClass(level);
    },
    igaLabel(level) {
      if (level === "critico") return "Crítico";
      if (level === "alto") return "Alto";
      if (level === "moderado") return "Moderado";
      return "Baixo";
    },
  },
};
</script>

<style scoped>
.attention-panel-open-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 7px 18px;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}
.attention-panel-open-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.4);
}
.attention-panel-open-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-badge 2s infinite;
}
@keyframes pulse-badge {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
}

.attention-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
}

/* FIX SCROLL: height 100% para o container saber seu limite */
.attention-panel-container {
  background: #f8fafc;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: panelSlideIn 0.3s ease-out;
  overflow: visible;
}
@keyframes panelSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.attention-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: white;
  flex-shrink: 0; /* FIX SCROLL: header não encolhe */
}
.attention-panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.attention-panel-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
}
.attention-panel-subtitle {
  font-size: 13px;
  opacity: 0.85;
}
.attention-panel-close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.attention-panel-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* FIX SCROLL: flex:1 + min-height:0 é o par correto em flexbox.
   Sem min-height:0, o filho cresce além do pai e overflow nunca ativa. */
.attention-panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.attention-panel-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  flex-shrink: 0;
}
.summary-card {
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.summary-number {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}
.summary-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.85;
}
.summary-critico {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #4c1d95;
  border: 1px solid #a78bfa;
}
.summary-alto {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #991b1b;
  border: 1px solid #f87171;
}
.summary-queda {
  background: linear-gradient(135deg, #fef3c7, #fed7aa);
  color: #92400e;
  border: 1px solid #f59e0b;
}
.summary-nutricao {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
  border: 1px solid #60a5fa;
}

.attention-panel-filters {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
}
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.filter-icon {
  color: #94a3b8;
  font-size: 11px;
}

.attention-panel-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #64748b;
  font-size: 15px;
  font-weight: 500;
}

/* FIX SCROLL: flex-shrink:0 impede a tabela de encolher.
   O scroll acontece no .attention-panel-body, não aqui. */
.attention-panel-table-wrapper {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  flex-shrink: 0;
}
.attention-panel-table {
  width: 100%;
  border-collapse: collapse;
}
.attention-panel-table thead tr {
  background: #f1f5f9;
  border-bottom: 2px solid #e2e8f0;
}
.attention-panel-table th {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
}
.attention-panel-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}
.attention-panel-table tbody tr:hover {
  background: #f8fafc;
}
.attention-panel-table tbody tr:last-child {
  border-bottom: none;
}
.attention-panel-table td {
  padding: 12px 16px;
  vertical-align: middle;
}

.row-critico {
  border-left: 4px solid #7c3aed;
}
.row-alto {
  border-left: 4px solid #dc2626;
}
.row-moderado {
  border-left: 4px solid #d97706;
}

.resident-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.resident-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
}
.resident-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.resident-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.resident-sector {
  font-size: 11px;
  color: #94a3b8;
}

.risk-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.risk-chip small {
  font-size: 10px;
  opacity: 0.75;
  font-weight: 600;
}

.chip-verde {
  background: #d1fae5;
  color: #065f46;
}
.chip-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.chip-vermelho {
  background: #fee2e2;
  color: #991b1b;
}
.chip-critico {
  background: #ede9fe;
  color: #4c1d95;
}

.col-iga {
  text-align: center;
}
.iga-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.iga-value {
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}
.iga-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.iga-critico {
  color: #4c1d95;
}
.iga-alto {
  color: #991b1b;
}
.iga-moderado {
  color: #92400e;
}
.iga-baixo {
  color: #065f46;
}

.col-action {
  text-align: right;
}
.btn-ver-residente {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 8px;
  background: #0f65b0;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.btn-ver-residente:hover {
  background: #0d5697;
  transform: translateY(-1px);
}

.attention-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #94a3b8;
  gap: 12px;
  font-size: 15px;
}
.attention-panel-empty i {
  font-size: 3rem;
  opacity: 0.4;
}

@media (max-width: 768px) {
  .attention-panel-summary {
    grid-template-columns: repeat(2, 1fr);
  }
  .attention-panel-body {
    padding: 16px;
  }
  .attention-panel-header {
    padding: 14px 16px;
  }
  .col-iga .iga-value {
    font-size: 18px;
  }
  .attention-panel-filters {
    flex-direction: column;
  }
}
@media (max-width: 480px) {
  .attention-panel-summary {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>